import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';

const RoseRainGame = () => {
  // --- Game Config ---
  const GAME_WIDTH = window.innerWidth > 500 ? 400 : window.innerWidth;
  const GAME_HEIGHT = window.innerHeight > 800 ? 700 : window.innerHeight * 0.8;
  const PLAYER_SIZE = 50;
  const ITEM_SIZE = 40;
  const SPAWN_RATE = 40;
  
  // --- Refs ---
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const frameCountRef = useRef(0);
  const scoreRef = useRef(0);
  const hpRef = useRef(3);
  const playerXRef = useRef(GAME_WIDTH / 2);
  const gameStateRef = useRef('START');
  const moonPhaseRef = useRef('NORMAL');
  const moonTimerRef = useRef(0);
  const itemsRef = useRef([]);

  // --- State ---
  const [uiState, setUiState] = useState('START');
  const [finalScore, setFinalScore] = useState(0);

  // --- Helpers ---
  const randomRange = (min, max) => Math.random() * (max - min) + min;

  // --- Game Functions (ต้องประกาศก่อน useEffect หรือภายใน Component) ---
  
  const startGame = () => {
    scoreRef.current = 0;
    hpRef.current = 3;
    itemsRef.current = [];
    moonPhaseRef.current = 'NORMAL';
    moonTimerRef.current = 0;
    
    gameStateRef.current = 'PLAYING';
    setUiState('PLAYING');
  };

  const endGame = () => {
    gameStateRef.current = 'GAMEOVER';
    setFinalScore(scoreRef.current);
    setUiState('GAMEOVER');
  };

  const switchMoonPhase = () => {
    const current = moonPhaseRef.current;
    if (current === 'NORMAL') {
        moonPhaseRef.current = Math.random() > 0.5 ? 'FULL' : 'NEW';
    } else {
        moonPhaseRef.current = 'NORMAL';
    }
  };

  const spawnItem = () => {
    const phase = moonPhaseRef.current;
    let type = 'ROSE'; 
    let speed = randomRange(3, 5);

    if (phase === 'NORMAL') {
        type = Math.random() > 0.2 ? 'ROSE' : 'THORN';
    } else if (phase === 'FULL') {
        type = Math.random() > 0.5 ? 'GOLD_ROSE' : 'STAR';
        speed += 2;
    } else if (phase === 'NEW') {
        type = 'FIRE';
        speed += 3;
    }

    itemsRef.current.push({
        x: randomRange(20, GAME_WIDTH - 20),
        y: -50,
        type: type,
        speed: speed
    });
  };

  const handleCollect = (item) => {
    if (item.type === 'ROSE') scoreRef.current += 10;
    else if (item.type === 'GOLD_ROSE') scoreRef.current += 50;
    else if (item.type === 'STAR') scoreRef.current += 30;
    else if (item.type === 'THORN' || item.type === 'FIRE') {
        hpRef.current -= 1;
    }
  };

  const handleMiss = (item) => {
    if (item.type === 'ROSE' || item.type === 'GOLD_ROSE') {
        hpRef.current -= 1;
    }
  };

  const updateGame = () => {
    if (gameStateRef.current !== 'PLAYING') return;

    frameCountRef.current++;

    // Moon Phase Logic
    moonTimerRef.current++;
    if (moonTimerRef.current > 600) {
        moonTimerRef.current = 0;
        switchMoonPhase();
    }

    // Spawn Logic
    if (frameCountRef.current % SPAWN_RATE === 0) {
        spawnItem();
    }

    // Move & Collision Logic
    itemsRef.current.forEach((item, index) => {
      item.y += item.speed;

      const dist = Math.abs(item.x - playerXRef.current);
      const isHitY = item.y > GAME_HEIGHT - PLAYER_SIZE - 20 && item.y < GAME_HEIGHT;
      
      if (isHitY && dist < PLAYER_SIZE / 1.5) {
        handleCollect(item);
        itemsRef.current.splice(index, 1);
      } else if (item.y > GAME_HEIGHT) {
        handleMiss(item);
        itemsRef.current.splice(index, 1);
      }
    });

    if (hpRef.current <= 0) {
        endGame();
    }
  };

  const drawGame = (ctx) => {
    const phase = moonPhaseRef.current;
    
    // Background
    if (phase === 'NORMAL') ctx.fillStyle = '#1e293b';
    else if (phase === 'FULL') ctx.fillStyle = '#0f172a';
    else if (phase === 'NEW') ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Moon Indicator
    ctx.font = '30px Arial';
    ctx.textAlign = 'right';
    let moonIcon = '🌒';
    let phaseText = 'ปกติ';
    if (phase === 'FULL') { moonIcon = '🌕'; phaseText = 'โบนัส!'; ctx.fillStyle = '#fbbf24'; }
    else if (phase === 'NEW') { moonIcon = '🌑'; phaseText = 'หนีไป!'; ctx.fillStyle = '#ef4444'; }
    else ctx.fillStyle = '#ffffff';
    ctx.fillText(`${moonIcon} ${phaseText}`, GAME_WIDTH - 20, 40);

    // Player
    ctx.font = `${PLAYER_SIZE}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('👸', playerXRef.current, GAME_HEIGHT - 10);

    // Items
    itemsRef.current.forEach(item => {
        let icon = '';
        switch(item.type) {
            case 'ROSE': icon = '🌹'; break;
            case 'THORN': icon = '🌵'; break;
            case 'GOLD_ROSE': icon = '💐'; break;
            case 'STAR': icon = '✨'; break;
            case 'FIRE': icon = '🔥'; break;
            default: icon = '❓';
        }
        ctx.font = `${ITEM_SIZE}px Arial`;
        ctx.fillText(icon, item.x, item.y);
    });

    // HUD
    ctx.fillStyle = 'white';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`Score: ${scoreRef.current}`, 20, 40);

    let hearts = '';
    for(let i=0; i<hpRef.current; i++) hearts += '❤️';
    ctx.fillText(hearts, 20, 70);
  };

  // --- Effect Loop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const handleInput = (clientX) => {
      if (gameStateRef.current !== 'PLAYING') return;
      const rect = canvas.getBoundingClientRect();
      let x = clientX - rect.left;
      if (x < PLAYER_SIZE / 2) x = PLAYER_SIZE / 2;
      if (x > GAME_WIDTH - PLAYER_SIZE / 2) x = GAME_WIDTH - PLAYER_SIZE / 2;
      playerXRef.current = x;
    };

    const onTouchMove = (e) => handleInput(e.touches[0].clientX);
    const onMouseMove = (e) => handleInput(e.clientX);

    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('mousemove', onMouseMove);

    const render = () => {
      updateGame();
      drawGame(ctx);
      requestRef.current = requestAnimationFrame(render);
    };

    requestRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(requestRef.current);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('mousemove', onMouseMove);
    };
  }, []); // Empty dependency array is fine here because refs are mutable

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 p-4 font-sans text-slate-100 relative">
      
      {/* Back Buttons */}
      <div className="absolute top-4 left-4 z-50 flex gap-2">
        <Link 
          to="/games" 
          className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-md rounded-full shadow-lg text-white hover:bg-white hover:text-slate-900 transition-all border border-white/20"
          title="กลับไปหน้ารวมเกม"
        >
          <ChevronLeft className="w-6 h-6" />
        </Link>
        <Link 
          to="/" 
          className="flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-md rounded-full shadow-lg text-white hover:bg-white hover:text-slate-900 transition-all border border-white/20"
          title="กลับหน้าหลักเว็บไซต์"
        >
          <Home className="w-5 h-5" />
        </Link>
      </div>

      <h1 className="text-2xl font-bold mb-4 text-pink-300 drop-shadow-lg z-10">
        🌹 ฝนกุหลาบ...หนามต้องห้าม
      </h1>

      <div className="relative shadow-2xl rounded-lg overflow-hidden border-4 border-slate-700 z-10">
        
        <canvas
          ref={canvasRef}
          width={GAME_WIDTH}
          height={GAME_HEIGHT}
          className="bg-slate-800 touch-none cursor-pointer"
        />

        {/* Start Screen */}
        {uiState === 'START' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-center p-6">
            <h2 className="text-3xl font-bold text-pink-500 mb-2">มัทนะพาธา</h2>
            <p className="text-sm text-gray-300 mb-6">
              ช่วยมัทนาเก็บกุหลาบให้ทัน<br/>
              ระวัง! ถ้ากุหลาบตกพื้น หรือโดนหนาม<br/>
              หัวใจจะแตกสลาย 💔
            </p>
            <div className="space-y-2 text-left bg-slate-800 p-4 rounded text-xs mb-6">
              <p>🌒 <b>ปกติ:</b> เก็บ 🌹 ระวัง 🌵</p>
              <p>🌕 <b>วันเพ็ญ:</b> เก็บ 💐✨ (โบนัส!)</p>
              <p>🌑 <b>จันทร์ดับ:</b> หลบ 🔥 ห้ามเก็บ!</p>
            </div>
            <button 
              onClick={startGame}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold rounded-full text-xl animate-bounce shadow-lg hover:scale-105 transition"
            >
              เริ่มเกม!
            </button>
          </div>
        )}

        {/* Game Over Screen */}
        {uiState === 'GAMEOVER' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-center p-6 z-10">
            <h2 className="text-4xl font-bold text-red-500 mb-2">จบเกม!</h2>
            <p className="text-xl text-white mb-6">คะแนนของคุณ: <span className="text-yellow-400 font-bold text-3xl">{finalScore}</span></p>
            
            <div className="flex gap-4">
                <button 
                onClick={startGame}
                className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 transition"
                >
                เล่นใหม่
                </button>
                <Link to="/games" className="px-6 py-2 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-500 transition">
                  ออกเกม
                </Link>
            </div>
          </div>
        )}

      </div>
      
      <p className="mt-4 text-xs text-slate-500 z-10">
        แตะและลาก เพื่อเคลื่อนที่ตัวละคร
      </p>
    </div>
  );
};

export default RoseRainGame;